import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Link } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent, useState } from "react";

interface LoginWithEmailAndPasswordFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  onForgotPassword: () => void;
  disabled?: boolean;
  errorMessage?: string;
}

/**
 * Email and password login form
 */
export function LoginWithEmailAndPasswordForm({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  onSubmit,
  onForgotPassword,
  disabled = false,
  errorMessage,
}: LoginWithEmailAndPasswordFormProps) {
  const { tu } = useTu("login");

  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text variant="body1" sx={{ textAlign: "center", color: "base.muted" }}>
        {tu("Sign in with email and password")}
      </Text>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Text semibold variant="body2" sx={{ color: "base.muted" }}>
            {tu("Email")}
          </Text>
          <TextInput
            fullWidth
            required
            type="email"
            placeholder="your.email@example.com"
            value={email}
            disabled={disabled || loading}
            autoComplete="email"
            inputProps={{ "aria-label": "Email" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1rem",
                backgroundColor: "background.standout",
              },
            }}
            onInput={(event) => {
              setEmail((event.target as HTMLInputElement).value);
            }}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text semibold variant="body2" sx={{ color: "base.muted" }}>
              {tu("Password")}
            </Text>
            <Link
              component="button"
              type="button"
              variant="caption"
              aria-disabled={disabled || loading ? true : undefined}
              underline="hover"
              sx={{
                color: "primary.main",
                padding: 0,
                border: 0,
                background: "none",
                fontFamily: "inherit",
                cursor: "pointer",
              }}
              onClick={onForgotPassword}
            >
              {tu("Set/reset password")}
            </Link>
          </Box>
          <TextInput
            fullWidth
            required
            type="password"
            value={password}
            disabled={disabled || loading}
            autoComplete="current-password"
            inputProps={{ "aria-label": "Password" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1rem",
                backgroundColor: "background.standout",
              },
            }}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </Box>
      </Box>
      <LoadingButton
        fullWidth
        type="submit"
        variant="primary"
        size="large"
        isLoading={isSubmitting || loading}
        disabled={disabled}
      >
        {tu("Sign In")}
      </LoadingButton>
      {errorMessage && (
        <Text
          variant="body2"
          role="alert"
          sx={{ color: "error.main", textAlign: "center", marginTop: 2 }}
        >
          {errorMessage}
        </Text>
      )}
    </Box>
  );
}
